import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface CaptchaModalProps {
  isOpen: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onVerify: (code: string) => void;
  // eslint-disable-next-line no-unused-vars
  onCaptchaGenerated?: (code: string) => void;
  showError?: boolean;
  onErrorClose?: () => void;
}

// Generate random CAPTCHA code
const generateCaptchaCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 6; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const CaptchaModal: React.FC<CaptchaModalProps> = ({
  isOpen,
  onClose,
  onVerify,
  onCaptchaGenerated,
  showError = false,
  onErrorClose
}) => {
  console.log('CaptchaModal render:', { isOpen, showError });
  
  const [code, setCode] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  // Generate new CAPTCHA code when modal opens
  useEffect(() => {
    if (isOpen) {
      const newCode = generateCaptchaCode();
      console.log('CAPTCHA Modal: Generated new code:', newCode);
      setCaptchaCode(newCode);
      setCode('');
      // Notify parent component of the new code
      if (onCaptchaGenerated) {
        console.log('CAPTCHA Modal: Notifying parent of new code:', newCode);
        onCaptchaGenerated(newCode);
      }
    }
  }, [isOpen, onCaptchaGenerated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;

    console.log('CAPTCHA Modal: Submitting code:', {
      submittedCode: code.trim(),
      displayedCode: captchaCode,
      match: code.trim() === captchaCode
    });

    setIsVerifying(true);
    await onVerify(code.trim());
    setIsVerifying(false);
  };

  const handleClose = () => {
    setCode('');
    onClose();
  };

  const handleRefreshCaptcha = () => {
    const newCode = generateCaptchaCode();
    console.log('CAPTCHA Modal: Refreshing code:', newCode);
    setCaptchaCode(newCode);
    setCode('');
    // Notify parent component of the new code
    if (onCaptchaGenerated) {
      console.log('CAPTCHA Modal: Notifying parent of refreshed code:', newCode);
      onCaptchaGenerated(newCode);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Desktop Captcha Modal */}
      <div className="fixed inset-0 bg-black bg-opacity-50 md:flex hidden items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 px-10 py-8 relative">
          {/* Header */}
          <div className="px-6 py-4">
            {/* <div className="flex items-center justify-between"> */}
              <h5 className="text-3xl font-light text-[var(--color-text)] text-center">Verify You&apos;re Human</h5>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors absolute right-8 top-7"
              >
                <X className="h-6 w-6" />
              </button>
            {/* </div> */}
            <p className="text-sm text-[var(--color-sub-title-text)] mt-1 text-center">
              Please complete the security check to continue
            </p>
          </div>

          {/* Error Message */}
          {showError && (
            <div className="px-6">
                <div className="px-2 py-4 flex items-center border border-dashed border-[#E67E7D] rounded-lg space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <div className="flex-1">
                    <p className="text-sm text-red-500">
                      Incorrect verification. <br /> 
                      <span className="text-[#00000066]">The code you entered doesn&apos;t match. Please try again with a new code.</span> 
                    </p>
                  </div>
                  <button
                    onClick={onErrorClose}
                    className="text-red-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* CAPTCHA Display */}
              <div className="text-center">
                <div style={{background: 'linear-gradient(270deg, rgba(255, 255, 255, 0.5) 0%, rgba(196, 219, 216, 0.5) 100%)'}}  className="border-2 border-dashed border-[#4C5C684D] rounded-lg px-8 py-4">
                  <div className="text-[34px] font-bold text-[var(--color-text)] tracking-wider">
                    {captchaCode}
                  </div>
                </div>
                {/* <p className="text-sm text-gray-600">
                  Enter the verification code shown above
                </p> */}
              </div>

              {/* Input Field */}
              <div>
                <label htmlFor="captcha-code" className="block text-base font-normal text-gray-900 mb-2">
                  Verification Code
                </label>
                <input
                  id="captcha-code"
                  type="text"
                  value={code}
                  minLength={6}
                  onChange={(e) => {
                    const newValue = e.target.value.toUpperCase();
                    console.log('CAPTCHA input changed:', {
                      oldValue: code,
                      newValue: newValue,
                      displayedCode: captchaCode,
                      match: newValue === captchaCode
                    });
                    setCode(newValue);
                  }} 
                  placeholder="Enter Code"
                  className={`w-full px-4 py-3 bg-[#F5F5F5] border outline-none rounded-lg focus:border-[#22c55e] text-md tracking-wider uppercase ${showError && 'border-red-500'}`}
                  maxLength={6}
                  autoFocus
                />
                {/* Debug: Show expected code */}
                {/* <div className="mt-2 text-xs text-gray-500 text-center">
                  Debug: Expected code is &quot;{captchaCode}&quot;
                </div> */}
              </div>

              {/* Action Button */}
              <button
                type="submit"
                disabled={!code.trim() || isVerifying}
                className="px-10 signin-button"
              >
                {isVerifying ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify and Continue'
                )}
              </button>
            </form>

            {/* Help Link */}
            <div className="mt-8 text-center text-base">
              <p
                className="text-[var(--color-text)] font-light"
              >
                Having trouble? 
                <button 
                  onClick= {handleRefreshCaptcha} 
                  className="text-[#217C70] hover:text-[#217c708c] underline font-bold ml-1" 
                >
                    Try Different Challenge  
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile, tablet Captcha Modal */}
      <div className='md:hidden flex flex-col items-center justify-center gap-4 max-w-full w-full mx-6'>
        {/* Header */}
        <div className="px-6 py-4">
            <img src={`${process.env.PUBLIC_URL}/SmallScreenImages/CaptchaVerifyImage.svg`} alt="Captcha Verify Image" className='w-16 h-16 mx-auto mb-8 object-cover' />
            <h5 className="sm:text-4xl text-3xl font-light text-[var(--color-text)] text-center">Verify You&apos;re Human</h5>
            {/* <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors absolute right-8 top-7"
            >
              <X className="h-6 w-6" />
            </button> */}
          <p className="sm:text-base text-sm text-[var(--color-sub-title-text)] mt-1 text-center">
            Please complete the security check to continue
          </p>
        </div>
        <div className="bg-white rounded-[1.25rem] w-full mx-4 p-6 relative">

          {/* Error Message */}
          {showError && (
            <div className="px-2 py-4 mb-4 flex items-center border border-dashed border-[#E67E7D] rounded-lg space-x-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <div className="flex-1">
                <p className="text-sm text-red-500">
                  Incorrect verification. <br /> 
                  <span className="text-[#00000066]">The code you entered doesn&apos;t match. Please try again with a new code.</span> 
                </p>
              </div>
              <button
                onClick={onErrorClose}
                className="text-red-400 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Content */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* CAPTCHA Display */}
              <div className="text-center">
                <div style={{background: 'linear-gradient(270deg, rgba(255, 255, 255, 0.5) 0%, rgba(196, 219, 216, 0.5) 100%)'}}  className="border-2 border-dashed border-[#4C5C684D] rounded-lg px-8 py-4">
                  <div className="text-[34px] font-bold text-[var(--color-text)] tracking-wider">
                    {captchaCode}
                  </div>
                </div>
                {/* <p className="text-sm text-gray-600">
                  Enter the verification code shown above
                </p> */}
              </div>

              {/* Input Field */}
              <div>
                <label htmlFor="captcha-code" className="block text-base font-normal text-gray-900 mb-2">
                  Verification Code
                </label>
                <input
                  id="captcha-code"
                  type="text"
                  value={code}
                  minLength={6}
                  onChange={(e) => {
                    const newValue = e.target.value.toUpperCase();
                    console.log('CAPTCHA input changed:', {
                      oldValue: code,
                      newValue: newValue,
                      displayedCode: captchaCode,
                      match: newValue === captchaCode
                    });
                    setCode(newValue);
                  }} 
                  placeholder="Enter Code"
                  className={`w-full px-4 py-3 bg-[#F5F5F5] border outline-none rounded-lg focus:border-[#22c55e] text-md tracking-wider uppercase ${showError && 'border-red-500'}`}
                  maxLength={6}
                  autoFocus
                />
                {/* Debug: Show expected code */}
                {/* <div className="mt-2 text-xs text-gray-500 text-center">
                  Debug: Expected code is &quot;{captchaCode}&quot;
                </div> */}
              </div>

              {/* Action Button */}
              <button
                type="submit"
                disabled={!code.trim() || isVerifying}
                className="px-10 signin-button"
              >
                {isVerifying ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Verifying...</span>
                  </div>
                ) : (
                  'Verify and Continue'
                )}
              </button>
            </form>

            {/* Help Link */}
            <div className="mt-8 mb-4 text-center text-base">
              <p
                className="text-[var(--color-text)] font-light"
              >
                Having trouble? 
                <button 
                  onClick= {handleRefreshCaptcha} 
                  className="text-[#217C70] hover:text-[#217c708c] underline font-bold ml-1" 
                >
                    Try Different Challenge  
                </button>
              </p>
            </div>
        </div>
      </div>
    </>
  );
};

export default CaptchaModal; 